import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { usePatientMutations } from '@/features/patient-record/hooks/usePatientMutations';
import type {
  ContactKind,
  ContactPointViewModel,
  ContactUse,
  DemographicsViewModel,
} from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from "../_shared/CollapsibleCard";
import { ConfirmDeleteDialog } from "../_shared/ConfirmDeleteDialog";
import { useFocusOnMount } from "../_shared/useFocusOnMount";

/** Selectable kinds for newly-created or retyped contact points. SMS was
 *  removed for new entries; pre-existing SMS rows still render and remain
 *  editable (the editor preserves the original kind as a fallback option). */
const SELECTABLE_KINDS: readonly ContactKind[] = ['EMAIL', 'PHONE'];
/** Phone subtypes offered to clinicians. The BE accepts HOME/WORK/MOBILE/OLD/TEMP;
 *  we only surface the three the product spec calls out. Legacy rows carrying
 *  OLD/TEMP are still editable — the editor preserves the original `use` as
 *  a fallback option (same pattern as the SMS preservation above). */
const SELECTABLE_PHONE_USES: readonly ContactUse[] = ['HOME', 'MOBILE', 'WORK'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_ALLOWED = /^[0-9+\-()\s.]+$/;

interface ContactPointsCardProps {
  readonly guid: string;
  readonly demographics: DemographicsViewModel;
}

export function ContactPointsCard({
  guid,
  demographics,
}: ContactPointsCardProps): ReactElement {
  const { t } = useTranslation();
  const { updateDemographics } = usePatientMutations(guid);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const persist = (contactPoints: readonly ContactPointViewModel[]): void => {
    updateDemographics.mutate({ ...demographics, contactPoints });
  };

  /** When a row becomes preferred, every other row drops the flag — only
   *  one contact point may carry it at a time. */
  const enforceSinglePreferred = (
    list: readonly ContactPointViewModel[],
    preferredIndex: number,
  ): ContactPointViewModel[] =>
    list.map((entry, i) => (i === preferredIndex ? entry : { ...entry, preferred: false }));

  const saveEdit = (index: number, next: ContactPointViewModel): void => {
    let list = demographics.contactPoints.map((entry, i) => (i === index ? next : entry));
    if (next.preferred) list = enforceSinglePreferred(list, index);
    persist(list);
    setEditingIndex(null);
  };

  const saveNew = (next: ContactPointViewModel): void => {
    const nextIndex = demographics.contactPoints.length;
    let list: readonly ContactPointViewModel[] = [...demographics.contactPoints, next];
    if (next.preferred) list = enforceSinglePreferred(list, nextIndex);
    persist(list);
    setCreating(false);
  };

  const requestDelete = demographics.contactPoints[pendingDeleteIndex ?? -1] ?? null;
  const showEmpty = demographics.contactPoints.length === 0 && !creating;

  const summaryRows: CollapsibleCardSummaryRow[] = demographics.contactPoints.map((contact) => {
    const value =
      contact.type === 'PHONE' && contact.use !== null
        ? `${contact.value} (${t(`patient.contactUse.${contact.use}`)})`
        : contact.value;
    return {
      label: t(`patient.contactType.${contact.type}`),
      value: value.length > 0 ? value : '—',
      filled: contact.value.length > 0,
    };
  });

  return (
    <>
      <CollapsibleCard
        idPrefix="demographics"
        anchorId="contact"
        title={t('patient.demographics.contact')}
        count={demographics.contactPoints.length}
        summary={summaryRows}
      >
        {showEmpty ? (
          <p className="text-sm text-muted-foreground italic mb-3">
            {t('patient.demographics.noContactPoints')}
          </p>
        ) : (
          <ul className="mb-3">
            {demographics.contactPoints.map((contact, index) => (
              <li key={`${contact.type}:${contact.value}:${String(index)}`}>
                {editingIndex === index ? (
                  <ContactPointEditor
                    contact={contact}
                    saving={updateDemographics.isPending}
                    onSave={(next) => {
                      saveEdit(index, next);
                    }}
                    onCancel={() => {
                      setEditingIndex(null);
                    }}
                  />
                ) : (
                  <ContactPointRow
                    contact={contact}
                    onEdit={() => {
                      setEditingIndex(index);
                    }}
                    onDelete={() => {
                      setPendingDeleteIndex(index);
                    }}
                  />
                )}
              </li>
            ))}
            {creating && (
              <li>
                <ContactPointEditor
                  contact={{ type: 'EMAIL', value: '', preferred: false, use: null }}
                  saving={updateDemographics.isPending}
                  onSave={saveNew}
                  onCancel={() => {
                    setCreating(false);
                  }}
                />
              </li>
            )}
          </ul>
        )}
        {!creating && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(true);
              setEditingIndex(null);
            }}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('patient.demographics.addContactPoint')}
          </Button>
        )}
      </CollapsibleCard>
      {requestDelete !== null && pendingDeleteIndex !== null && (
        <ConfirmDeleteDialog
          title={t('patient.demographics.confirmDeleteContactTitle')}
          message={t('patient.demographics.confirmDeleteContactMessage', {
            value: requestDelete.value,
          })}
          onCancel={() => {
            setPendingDeleteIndex(null);
          }}
          onConfirm={() => {
            const list = demographics.contactPoints.filter(
              (_, i) => i !== pendingDeleteIndex,
            );
            persist(list);
            setPendingDeleteIndex(null);
          }}
        />
      )}
    </>
  );
}

interface ContactPointRowProps {
  readonly contact: ContactPointViewModel;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function ContactPointRow({ contact, onEdit, onDelete }: ContactPointRowProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
      className="group flex items-center gap-3 cursor-pointer py-2 border-b border-dashed border-border last:border-b-0 hover:bg-brand-soft/40 transition-colors -mx-1 px-1"
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground w-14 shrink-0">
        {t(`patient.contactType.${contact.type}`)}
      </span>
      <span className="text-[13px] text-foreground flex-1 break-all">
        {contact.value}
        {contact.type === 'PHONE' && contact.use !== null && (
          <span className="ml-2 text-[11px] text-muted-foreground">
            ({t(`patient.contactUse.${contact.use}`)})
          </span>
        )}
      </span>
      {contact.preferred && (
        <span className="text-[10px] uppercase tracking-wide text-primary font-semibold shrink-0">
          {t('patient.demographics.preferred')}
        </span>
      )}
      <span className="flex items-center gap-0.5 shrink-0 opacity-70 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          aria-label={t('patient.demographics.editContact')}
          className="h-7 w-7 p-0"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={t('patient.demographics.deleteContact')}
          className="h-7 w-7 p-0"
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        </Button>
      </span>
    </div>
  );
}

function validateContact(
  type: ContactKind,
  value: string,
): { readonly valid: boolean; readonly errorKey: string | null } {
  const trimmed = value.trim();
  if (trimmed.length === 0) return { valid: false, errorKey: null };
  if (type === 'EMAIL') {
    return EMAIL_PATTERN.test(trimmed)
      ? { valid: true, errorKey: null }
      : { valid: false, errorKey: 'patient.demographics.invalidEmail' };
  }
  if (type === 'PHONE') {
    if (!PHONE_ALLOWED.test(trimmed) || !/\d/.test(trimmed)) {
      return { valid: false, errorKey: 'patient.demographics.invalidPhone' };
    }
  }
  return { valid: true, errorKey: null };
}

interface ContactPointEditorProps {
  readonly contact: ContactPointViewModel;
  readonly saving: boolean;
  readonly onSave: (next: ContactPointViewModel) => void;
  readonly onCancel: () => void;
}

function ContactPointEditor({
  contact,
  saving,
  onSave,
  onCancel,
}: ContactPointEditorProps): ReactElement {
  const { t } = useTranslation();
  const [touched, setTouched] = useState(false);
  const inputRef = useFocusOnMount<HTMLInputElement>();

  // PTV2-1327: the primitive owns the draft + dirty registration (warn on nav / view-mode / refresh
  // while unsaved, with the required-field gate). Persistence is delegated to the parent card's
  // `onSave`, which also collapses this editor — so autosave stays OFF (an idle commit would tear the
  // row down mid-edit and fire a whole-demographics replace). The row is saved only by the explicit
  // Confirm button (or the nav-guard teardown flush, which commits through the same `onSave`).
  const form = useEditableDraft<ContactPointViewModel>({
    initial: contact,
    label: t('patient.demographics.contactFormLabel', { defaultValue: 'Contact information' }),
    autosave: false,
    canSave: (d) => validateContact(d.type, d.value).valid,
    onSave: (d) => {
      onSave({ ...d, value: d.value.trim() });
    },
  });
  const { draft } = form;

  // Existing SMS rows keep SMS available; new rows only see Email/Phone.
  const kindOptions: readonly ContactKind[] =
    !SELECTABLE_KINDS.includes(contact.type)
      ? [contact.type, ...SELECTABLE_KINDS]
      : SELECTABLE_KINDS;

  // Same pattern as kindOptions: a legacy row with use=OLD/TEMP keeps that
  // option available so editing other fields doesn't clobber the existing use.
  const useOptions: readonly ContactUse[] =
    contact.use !== null && !SELECTABLE_PHONE_USES.includes(contact.use)
      ? [contact.use, ...SELECTABLE_PHONE_USES]
      : SELECTABLE_PHONE_USES;

  const validation = validateContact(draft.type, draft.value);
  const canSave = validation.valid;
  const errorKey = touched ? validation.errorKey : null;

  const submitAndClose = (): void => {
    setTouched(true);
    if (!canSave) return;
    onSave({ ...draft, value: draft.value.trim() });
  };

  return (
    <form
      ref={form.containerProps.ref}
      className="flex flex-col gap-2 rounded-md border border-input bg-background p-2"
      onSubmit={(event) => {
        event.preventDefault();
        submitAndClose();
      }}
    >
      <div className="flex items-center gap-2">
        <Select
          value={draft.type}
          className="h-8 text-xs w-28 shrink-0"
          aria-label={t('patient.demographics.contactType')}
          onChange={(event) => {
            form.setDraft((d) => ({ ...d, type: event.target.value as ContactKind }));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
        >
          {kindOptions.map((option) => (
            <option key={option} value={option}>
              {t(`patient.contactType.${option}`)}
            </option>
          ))}
        </Select>
        <Input
          ref={inputRef}
          value={draft.value}
          className="h-8 text-sm flex-1"
          placeholder={
            draft.type === 'EMAIL'
              ? t('patient.demographics.emailValuePlaceholder')
              : draft.type === 'PHONE'
                ? t('patient.demographics.phoneValuePlaceholder')
                : t('patient.demographics.contactValuePlaceholder')
          }
          aria-invalid={errorKey !== null}
          inputMode={draft.type === 'PHONE' ? 'tel' : draft.type === 'EMAIL' ? 'email' : 'text'}
          onChange={(event) => {
            const raw = event.target.value;
            // Phone: filter out disallowed characters as the user types so the
            // input mirrors the validation contract instead of relying on
            // submit-time rejection.
            form.setDraft((d) => ({
              ...d,
              value: d.type === 'PHONE' ? raw.replace(/[^0-9+\-()\s.]/g, '') : raw,
            }));
          }}
          onBlur={() => {
            setTouched(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
        />
      </div>
      {draft.type === 'PHONE' && (
        <Select
          value={draft.use ?? 'HOME'}
          className="h-8 text-xs w-32 self-start"
          aria-label={t('patient.demographics.phoneUse')}
          onChange={(event) => {
            form.setDraft((d) => ({ ...d, use: event.target.value as ContactUse }));
          }}
        >
          {useOptions.map((option) => (
            <option key={option} value={option}>
              {t(`patient.contactUse.${option}`)}
            </option>
          ))}
        </Select>
      )}
      {errorKey !== null && (
        <p className="text-[11px] text-destructive" role="alert">
          {t(errorKey)}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={draft.preferred}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, preferred: event.target.checked }));
            }}
            className="h-3.5 w-3.5"
          />
          {t('patient.demographics.preferred')}
        </label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={saving}
          >
            {t('patient.demographics.cancel')}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!canSave}
            loading={saving}
            loadingText={t('patient.dirtyFooter.saving')}
          >
            {t('patient.demographics.confirm')}
          </Button>
        </div>
      </div>
    </form>
  );
}
